// Bulk optimiser for images that are already live in Firebase Storage.
//
// New uploads are optimised on the way in (see imageOptimizer.js), but anything
// uploaded before that existed is still a multi-megabyte camera photo. This walks
// every image referenced by Firestore, re-encodes the oversized ones to WebP and
// repoints the documents at the smaller copies.
//
// Deliberately never deletes the original object from Storage: the old file simply
// stops being referenced. Storage is cheap, bandwidth is what costs the customer
// their loading time, and keeping the original makes every run reversible.

import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, getMetadata, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { optimizeImage } from './imageOptimizer';

// Mirrors UPLOAD_PROFILES in Admin.js: cards render ~400px wide, menu pages
// open fullscreen and get pinch-zoomed to read printed text.
const PROFILES = {
	'menu-page-images': { maxWidth: 2000, maxHeight: 2000, targetBytes: 500 * 1024 },
	'product-images': { maxWidth: 1400, maxHeight: 1400, targetBytes: 260 * 1024 },
	'category-images': { maxWidth: 1400, maxHeight: 1400, targetBytes: 260 * 1024 }
};
const DEFAULT_PROFILE = PROFILES['category-images'];

// Re-encoding an image that is already small trades real quality for a few KB,
// so a file only becomes a candidate once it is clearly over its budget.
const SAVING_MARGIN = 1.15;

function isStorageUrl(url) {
	return typeof url === 'string' && /^https?:\/\/firebasestorage\.googleapis\.com\//.test(url);
}

// ".../o/category-images%2F1766_a.jpg?alt=media&token=..." -> "category-images/1766_a.jpg"
export function storagePathFromUrl(url) {
	try {
		const afterO = String(url).split('/o/')[1];
		if (!afterO) return null;
		return decodeURIComponent(afterO.split('?')[0]);
	} catch {
		return null;
	}
}

function folderOf(path) {
	if (!path || !path.includes('/')) return null;
	return path.split('/')[0];
}

export function profileForPath(path) {
	return PROFILES[folderOf(path)] || DEFAULT_PROFILE;
}

function baseNameOf(path) {
	const raw = (path || 'image').split('/').pop() || 'image';
	// Strip the "<timestamp>_" prefix a previous upload added, so repeated runs
	// don't accumulate 1766..._1786..._name.jpg
	return raw.replace(/^\d{10,}_/, '');
}

/**
 * Finds every Storage image referenced by Firestore and measures it.
 *
 * References are grouped by URL: the same photo can be used by several documents,
 * and it must be encoded once and then written to all of them.
 */
export async function scanStorageImages() {
	const [catSnap, prodSnap] = await Promise.all([
		getDocs(collection(db, 'categories')),
		getDocs(collection(db, 'products'))
	]);

	const byUrl = new Map();
	const addRef = (url, reference) => {
		if (!isStorageUrl(url)) return;
		if (!byUrl.has(url)) byUrl.set(url, { url, path: storagePathFromUrl(url), refs: [] });
		byUrl.get(url).refs.push(reference);
	};

	catSnap.docs.forEach(d => {
		const data = d.data() || {};
		const label = data.name_ro || (typeof data.name === 'string' ? data.name : data.name?.ro) || d.id;
		addRef(data.imageUrl, { collection: 'categories', docId: d.id, field: 'imageUrl', label });
		if (Array.isArray(data.menuPageImages)) {
			data.menuPageImages.forEach((u, index) => {
				addRef(u, { collection: 'categories', docId: d.id, field: 'menuPageImages', index, label: `${label} (menu page ${index + 1})` });
			});
		}
	});

	prodSnap.docs.forEach(d => {
		const data = d.data() || {};
		const label = data.name_ro || (typeof data.name === 'string' ? data.name : data.name?.ro) || d.id;
		addRef(data.imageUrl, { collection: 'products', docId: d.id, field: 'imageUrl', label });
	});

	const items = await Promise.all(Array.from(byUrl.values()).map(async entry => {
		const profile = profileForPath(entry.path);
		const item = {
			...entry,
			profile,
			size: 0,
			contentType: '',
			needsWork: false,
			error: null
		};
		try {
			const meta = await getMetadata(ref(storage, entry.url));
			item.size = Number(meta.size) || 0;
			item.contentType = meta.contentType || '';
		} catch (e) {
			item.error = 'Could not read this image’s details';
			return item;
		}
		item.needsWork = item.size > profile.targetBytes * SAVING_MARGIN;
		return item;
	}));

	items.sort((a, b) => b.size - a.size);
	return items;
}

async function writeReference(reference, newUrl) {
	const target = doc(db, reference.collection, reference.docId);
	if (reference.field === 'menuPageImages') {
		// Re-read so a concurrent edit to a different page isn't clobbered.
		const snap = await getDoc(target);
		if (!snap.exists()) throw new Error('document no longer exists');
		const current = Array.isArray(snap.data().menuPageImages) ? [...snap.data().menuPageImages] : [];
		if (reference.index >= current.length) throw new Error('menu page no longer exists');
		current[reference.index] = newUrl;
		await updateDoc(target, { menuPageImages: current });
		return;
	}
	await updateDoc(target, { [reference.field]: newUrl });
}

/**
 * Re-encodes one already-uploaded image and repoints every document that uses it.
 *
 * Storage is written before Firestore, so a failure part-way leaves the documents
 * pointing at the original, still-valid image rather than at nothing.
 */
export async function optimizeExistingImage(item) {
	const res = await fetch(item.url, { mode: 'cors', cache: 'no-store' });
	if (!res.ok) throw new Error(`download failed (${res.status})`);
	const blob = await res.blob();

	const name = baseNameOf(item.path);
	const original = new File([blob], name, { type: blob.type || item.contentType || 'image/jpeg' });

	const opt = await optimizeImage(original, item.profile);
	if (!opt.converted) {
		return { skipped: true, reason: opt.skipped || 'already-optimal', before: original.size, after: original.size };
	}
	// Guard against churn: rewriting for a trivial gain is not worth a re-encode.
	if (opt.optimizedSize > original.size * 0.9) {
		return { skipped: true, reason: 'not-worth-it', before: original.size, after: original.size };
	}

	const folder = folderOf(item.path) || 'category-images';
	const safeName = (opt.file.name || name).replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 120);
	const dest = ref(storage, `${folder}/${Date.now()}_${safeName}`);
	const uploaded = await uploadBytes(dest, opt.file, {
		contentType: opt.file.type,
		cacheControl: 'public,max-age=31536000,immutable'
	});
	const newUrl = await getDownloadURL(uploaded.ref);

	for (const reference of item.refs) {
		// eslint-disable-next-line no-await-in-loop
		await writeReference(reference, newUrl);
	}

	return {
		skipped: false,
		before: original.size,
		after: opt.optimizedSize,
		newUrl,
		outputType: opt.outputType,
		width: opt.width,
		height: opt.height,
		refsUpdated: item.refs.length
	};
}
